import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { fetchPublishedPosts, type UnifiedPost } from "@/lib/blog-db";
import { tagLabel, tagSlug } from "@/lib/blogTags";
import { absUrl, canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import heroImg from "@/assets/original/hero-shade.jpg";
import { ArrowRight, Calendar } from "lucide-react";

// `tag` is optional with NO default — a default value makes the router
// rewrite /blog to /blog?tag=, which Google then treats as a duplicate URL.
const searchSchema = z.object({
  tag: fallback(z.string(), "").optional(),
});


export const Route = createFileRoute("/blog/")({
  validateSearch: zodValidator(searchSchema),
  // Legacy filter URLs (/blog?tag=austin) permanently redirect to the
  // crawlable, self-canonical topic page so there is one indexable URL.
  beforeLoad: async ({ search, location }) => {
    if (!search.tag && /(?:^|[?&])tag=(?:&|$)/.test(location.searchStr)) {
      throw redirect({ to: "/blog", statusCode: 301 });
    }
    if (search.tag) {
      const requestedTag = tagSlug(search.tag);
      const posts = await fetchPublishedPosts();
      const hasMatchingPosts = posts.some((post) =>
        (post.tags ?? []).some((postTag) => tagSlug(postTag) === requestedTag),
      );

      if (!hasMatchingPosts) {
        throw redirect({ to: "/blog", statusCode: 301 });
      }

      throw redirect({
        to: "/blog/topic/$tag",
        params: { tag: requestedTag },
        statusCode: 301,
      });
    }
  },
  loader: async () => ({ posts: await fetchPublishedPosts() }),
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Blog — Texas Made Shade & Covers",
      description: "Tips, project stories, and how-tos from the Texas Made Shade & Covers shop — fabrics, welded aluminum frames, and 30+ years of Texas awning know-how.",
      image: heroImg,
      socialDescription:
        "Tips, project stories, and how-tos from the Texas Made Shade & Covers shop.",
    }),
    scripts: pageHeadScripts({
      path: "/blog",
      name: "Blog — Texas Made Shade & Covers",
      description: "Tips, project stories, and how-tos from the Texas Made Shade & Covers shop — fabrics, welded aluminum frames, and 30+ years of Texas awning know-how.",
    }),
  }),
  component: BlogIndex,
});

const FABRIC_CATEGORIES = [
  { tag: "awning", label: "Awning" },
  { tag: "marine", label: "Marine" },
  { tag: "upholstery", label: "Upholstery" },
];

function BlogIndex() {
  const { posts } = Route.useLoaderData() as { posts: UnifiedPost[] };
  const tag = Route.useSearch().tag ?? "";

  // Collect all tags present in posts so filters reflect actual content + custom tags.
  const allTags = Array.from(
    new Set(posts.flatMap((p) => p.tags ?? []).map((t) => tagSlug(t))),
  ).sort();
  const extraTags = allTags.filter(
    (t) => !FABRIC_CATEGORIES.some((c) => c.tag === t),
  );

  const filtered = tag
    ? posts.filter((p) =>
        (p.tags ?? []).some((t) => t.toLowerCase() === tag.toLowerCase()),
      )
    : posts;

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <section className="bg-navy-deep py-16 text-white">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs tracking-[0.3em] text-rust">
              ★ FROM THE SHOP ★
            </div>
            <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
              Texas Made Shade Blog
            </h1>
            <p className="mt-4 max-w-2xl text-white/80">
              Fabric tips, frame talk, and stories from real Texas projects.
            </p>
          </div>
        </section>

        <section className="border-b border-navy-deep/10 bg-white py-6">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-2 font-display text-[11px] uppercase tracking-[0.2em] text-navy-deep/60">
                Filter by fabric:
              </span>
              <FilterChip tag="" active={!tag} label="All" />
              {FABRIC_CATEGORIES.map((c) => (
                <FilterChip
                  key={c.tag}
                  tag={c.tag}
                  active={tag.toLowerCase() === c.tag}
                  label={c.label}
                />
              ))}
              {extraTags.length > 0 && (
                <>
                  <span className="mx-2 h-4 w-px bg-navy-deep/20" aria-hidden="true" />
                  {extraTags.map((t) => (
                    <FilterChip
                      key={t}
                      tag={t}
                      active={tag.toLowerCase() === t}
                      label={tagLabel(t)}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            {filtered.length === 0 ? (
              <p className="text-center text-navy-deep/70">
                No posts {tag ? `tagged "${tag}"` : ""} yet — check back soon.
              </p>
            ) : (
              <ul className="grid gap-8 md:grid-cols-2">
                {filtered.map((p) => (
                  <li
                    key={p.slug}
                    className="flex flex-col border-2 border-navy-deep/10 bg-white shadow-card"
                  >
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="block aspect-[16/9] overflow-hidden"
                    >
                      {p.coverImage && (
                        <img
                          src={absUrl(p.coverImage)}
                          alt={p.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition hover:scale-105"
                        />
                      )}
                    </Link>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-2 font-display text-[11px] uppercase tracking-[0.2em] text-navy-deep/60">
                        <Calendar className="h-3 w-3" aria-hidden="true" />
                        {new Date(p.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>
                      <h2 className="mt-2 font-display text-2xl font-bold text-navy-deep">
                        <Link
                          to="/blog/$slug"
                          params={{ slug: p.slug }}
                          className="hover:text-rust"
                        >
                          {p.title}
                        </Link>
                      </h2>
                      <p className="mt-3 flex-1 text-navy-deep/80">{p.excerpt}</p>
                      {p.tags && p.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {p.tags.map((t) => (
                            <Link
                              key={t}
                              to="/blog/topic/$tag"
                              params={{ tag: tagSlug(t) }}
                              className="border border-navy-deep/20 px-2 py-0.5 text-[11px] uppercase tracking-wider text-navy-deep/70 hover:border-rust hover:text-rust"
                            >
                              {t}
                            </Link>
                          ))}
                        </div>
                      )}
                      <Link
                        to="/blog/$slug"
                        params={{ slug: p.slug }}
                        className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-rust hover:text-navy-deep"
                      >
                        Read post <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}

function FilterChip({
  tag,
  active,
  label,
}: {
  tag: string;
  active: boolean;
  label: string;
}) {
  const linkProps = tag
    ? ({ to: "/blog/topic/$tag", params: { tag } } as const)
    : ({ to: "/blog" } as const);
  return (
    <Link
      {...linkProps}
      className={
        "border px-3 py-1 font-display text-[11px] uppercase tracking-[0.2em] transition " +
        (active
          ? "border-rust bg-rust text-white"
          : "border-navy-deep/20 text-navy-deep/70 hover:border-rust hover:text-rust")
      }
    >
      {label}
    </Link>
  );
}
